import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Coins, Phone, WifiHigh, Basket, GasPump, Palette, Sparkle, Tag, Gift,
  ArrowUp, ArrowDown, CheckCircle, Receipt, IdentificationCard, User,
  Flame, Trophy, Medal, ShieldCheck, Airplane, Compass, Lock,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shortDate } from "@/lib/format";
import { celebrateUnlocks } from "@/lib/celebrate";
import { Card, Loading, SectionTitle } from "@/components/riq";
import ReferralCard from "@/components/ReferralCard";
import { validateSAID } from "@/lib/saId";
import { trackConversion } from "@/lib/adsConversions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ICONS = { phone: Phone, wifi: WifiHigh, basket: Basket, gas: GasPump, palette: Palette, sparkle: Sparkle, tag: Tag };

export default function Rewards() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [game, setGame] = useState(null);
  const [redeeming, setRedeeming] = useState(null);
  const [confirm, setConfirm] = useState(null); // reward pending confirmation
  const [phone, setPhone] = useState("");
  const [idNumber, setIdNumber] = useState("");

  const load = () => api.get("/rewards").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => {
    load();
    api.get("/gamification").then((r) => {
      setGame(r.data);
      if (r.data?.newly_unlocked?.length) {
        celebrateUnlocks(r.data.newly_unlocked);
        load();
        refresh();
      }
    }).catch(() => {});
  }, []);

  const startRedeem = (reward) => {
    if (data.balance < reward.cost) return;
    setPhone(data.profile?.phone || "");
    setIdNumber(data.profile?.id_number || "");
    setConfirm(reward);
  };

  const redeem = async () => {
    const reward = confirm;
    if (!reward) return;
    if (!phone.trim() || !idNumber.trim()) { toast.error("Please fill in your phone and ID number."); return; }
    const idErr = validateSAID(idNumber);
    if (idErr) { toast.error(idErr); return; }
    setRedeeming(reward.id);
    try {
      const { data: res } = await api.post("/rewards/redeem", { reward_id: reward.id, phone: phone.trim(), id_number: idNumber.trim() });
      trackConversion("redemption");
      toast.success(`Redeemed ${reward.title}! We'll be in touch to fulfil it.`);
      setData((d) => ({
        ...d, balance: res.balance, redemptions: [res.redeemed, ...d.redemptions], claimed_ids: [...d.claimed_ids, reward.id],
        profile: { ...d.profile, phone: phone.trim(), id_number: idNumber.trim() },
        history: [{ type: "redeem", amount: -reward.cost, reason: `Redeemed ${reward.title}`, at: res.redeemed.at }, ...d.history],
      }));
      setConfirm(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not redeem");
    } finally { setRedeeming(null); }
  };

  if (data === null) return <Loading label="Loading your Moolas…" />;
  if (data === false) return <div className="text-muted-foreground">Could not load rewards.</div>;

  const vouchers = data.catalogue.filter((r) => r.type === "voucher");
  const perks = data.catalogue.filter((r) => r.type === "perk");
  const displayName = data.profile?.name || user?.name || user?.email;
  const idError = idNumber.trim() ? validateSAID(idNumber) : null;

  return (
    <div className="space-y-8">
      {/* Balance hero */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="bg-riq-primary text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.07] riq-noise" />
          <div className="relative flex items-center justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">Your Moolas</div>
              <div data-testid="moolas-balance" className="font-head text-5xl font-bold tracking-tight mt-2 text-white flex items-center gap-3">
                <Coins size={40} weight="fill" className="text-riq-gold" /> {data.balance.toLocaleString()}
              </div>
              <div className="text-white/70 text-sm mt-3">Earn <b className="text-riq-gold">{data.per_receipt} Moolas</b> for every unique receipt you scan.</div>
            </div>
            <Gift size={72} weight="duotone" className="text-white/15 hidden sm:block" />
          </div>
        </Card>
      </motion.div>

      {game && <GamificationPanel game={game} />}

      <ReferralCard />

      {/* Vouchers */}
      <div>
        <SectionTitle>Vouchers</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {vouchers.map((r) => <RewardCard key={r.id} reward={r} balance={data.balance} onRedeem={startRedeem} loading={redeeming === r.id} />)}
        </div>
      </div>

      {/* Perks */}
      <div>
        <SectionTitle>In-app Perks</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {perks.map((r) => <RewardCard key={r.id} reward={r} balance={data.balance} onRedeem={startRedeem} loading={redeeming === r.id} />)}
        </div>
      </div>

      {/* Your redemptions */}
      {data.redemptions?.length > 0 && (
        <div>
          <SectionTitle>Your redemptions</SectionTitle>
          <Card className="divide-y divide-riq-border p-0">
            {data.redemptions.map((r) => {
              const done = r.fulfilment_status === "fulfilled";
              return (
                <div key={r.id} data-testid={`my-redemption-${r.id}`} className="flex items-center gap-3 px-5 py-3.5">
                  <span className="w-9 h-9 rounded-xl bg-riq-sage flex items-center justify-center shrink-0"><Gift size={16} weight="duotone" className="text-riq-primary" /></span>
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{r.title}</div><div className="text-xs text-muted-foreground">{shortDate(r.at)} · {r.cost} Moolas</div></div>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold shrink-0 ${done ? "bg-[#E4F7ED] text-[#22B573]" : "bg-riq-gold/25 text-[#B25E00]"}`}>{done ? "Sent" : "Pending"}</span>
                </div>
              );
            })}
          </Card>
        </div>
      )}

      {/* History */}
      <div>
        <SectionTitle>Activity</SectionTitle>
        {data.history.length === 0 ? (
          <Card className="text-sm text-muted-foreground text-center py-8">No Moolas activity yet. Scan a receipt to start earning!</Card>
        ) : (
          <Card className="divide-y divide-riq-border p-0">
            {data.history.map((h, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-3.5">
                <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${h.type === "earn" ? "bg-riq-sage" : "bg-riq-accent/15"}`}>
                  {h.type === "earn" ? <ArrowUp size={16} weight="bold" className="text-riq-primary" /> : <ArrowDown size={16} weight="bold" className="text-riq-accent" />}
                </span>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{h.reason}</div><div className="text-xs text-muted-foreground">{shortDate(h.at)}</div></div>
                <div className={`font-head font-bold ${h.type === "earn" ? "text-riq-primary" : "text-riq-accent"}`}>{h.amount > 0 ? "+" : ""}{h.amount}</div>
              </div>
            ))}
          </Card>
        )}
      </div>

      {/* Redemption confirmation — collect fulfilment details */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="redeem-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gift size={20} weight="duotone" className="text-riq-primary" /> Redeem {confirm?.title}</DialogTitle>
            <DialogDescription>Confirm your details so we can deliver your reward. We'll send this to our team to fulfil.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-riq-sage/40 p-3 flex items-center gap-2 text-sm">
              <Coins size={18} weight="fill" className="text-riq-gold" />
              <span>Costs <b>{confirm?.cost} Moolas</b> · balance after: <b>{data.balance - (confirm?.cost || 0)}</b></span>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 flex items-center gap-1.5"><User size={13} /> Name</label>
              <input data-testid="redeem-name" value={displayName || ""} readOnly disabled className="riq-input w-full bg-secondary/50 cursor-not-allowed" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 flex items-center gap-1.5"><Phone size={13} /> Phone number</label>
              <input data-testid="redeem-phone" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="e.g. 082 123 4567" className="riq-input w-full" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1.5 flex items-center gap-1.5"><IdentificationCard size={13} /> ID number</label>
              <input data-testid="redeem-id" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} placeholder="13-digit SA ID number" maxLength={16}
                className={`riq-input w-full ${idError ? "!border-riq-accent" : ""}`} />
              {idError && <p data-testid="redeem-id-error" className="text-xs text-riq-accent mt-1.5">{idError}</p>}
            </div>
            <button
              data-testid="redeem-confirm-btn"
              onClick={redeem}
              disabled={redeeming || !phone.trim() || !idNumber.trim() || !!idError}
              className="w-full rounded-full bg-riq-primary text-white py-3 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5 transition-transform"
            >
              {redeeming ? "Redeeming…" : `Confirm & redeem for ${confirm?.cost} Moolas`}
            </button>
            <p className="text-[11px] text-muted-foreground text-center">Your phone and ID are saved to your profile for future redemptions.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ACH_ICONS = { receipt: Receipt, medal: Medal, flame: Flame, shield: ShieldCheck, airplane: Airplane, compass: Compass, coins: Coins };

function GamificationPanel({ game }) {
  const { streak, level, achievements, achievements_earned, achievements_total } = game;
  return (
    <div className="space-y-4" data-testid="gamification-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Streak */}
        <Card data-testid="streak-card" className="relative overflow-hidden">
          <div className="flex items-center gap-4">
            <div className={`grid place-items-center h-14 w-14 rounded-2xl shrink-0 ${streak.current > 0 ? "bg-riq-gold/15" : "bg-secondary"}`}>
              <Flame size={30} weight="fill" className={streak.current > 0 ? "text-riq-gold" : "text-neutral-300"} />
            </div>
            <div className="min-w-0">
              <div className="font-head text-3xl font-bold text-riq-primary leading-none" data-testid="streak-current">{streak.current} <span className="text-base font-semibold text-muted-foreground">day{streak.current === 1 ? "" : "s"}</span></div>
              <div className="text-xs text-muted-foreground mt-1">Scan streak · best {streak.best}</div>
              <div className="text-[11px] mt-1.5 font-medium">
                {streak.active_today
                  ? <span className="text-[#1f7a48]">Scanned today — keep it going!</span>
                  : <span className="text-riq-accent">Scan a receipt today to keep your streak</span>}
                {streak.freeze_used && <span className="text-muted-foreground"> · freeze used</span>}
              </div>
            </div>
          </div>
        </Card>

        {/* Level */}
        <Card data-testid="level-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trophy size={22} weight="fill" className="text-riq-gold" />
              <span className="font-head font-bold text-riq-primary">Level {level.level} · {level.name}</span>
            </div>
            <span className="text-xs font-semibold text-muted-foreground">{level.moolas} Moolas</span>
          </div>
          <div className="mt-3 h-2.5 rounded-full bg-secondary overflow-hidden">
            <div className="h-full rounded-full bg-riq-primary transition-[width] duration-700" style={{ width: `${Math.round(level.progress * 100)}%` }} />
          </div>
          <div className="text-[11px] text-muted-foreground mt-1.5">
            {level.is_max ? "Top level reached — you're a Sliply Legend!" : `${level.next_at - level.moolas} Moolas to ${level.next_name}`}
          </div>
        </Card>
      </div>

      {/* Achievements */}
      <div>
        <SectionTitle>Achievements <span className="text-sm font-normal text-muted-foreground">({achievements_earned}/{achievements_total})</span></SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {achievements.map((a) => {
            const Icon = ACH_ICONS[a.icon] || Medal;
            return (
              <div key={a.id} data-testid={`achievement-${a.id}`}
                className={`rounded-2xl border p-4 ${a.earned ? "border-riq-primary/30 bg-riq-primary/[0.04]" : "border-riq-border bg-white"}`}>
                <div className="flex items-center justify-between">
                  <span className={`grid place-items-center h-10 w-10 rounded-xl ${a.earned ? "bg-riq-primary text-white" : "bg-secondary text-neutral-300"}`}>
                    {a.earned ? <Icon size={20} weight="fill" /> : <Lock size={16} weight="bold" />}
                  </span>
                  {a.earned && <CheckCircle size={18} weight="fill" className="text-[#22B573]" />}
                </div>
                <div className={`font-head font-semibold text-sm mt-2.5 ${a.earned ? "text-riq-primary" : "text-neutral-500"}`}>{a.title}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{a.description}</div>
                {!a.earned && (
                  <div className="mt-2.5">
                    <div className="h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-riq-gold" style={{ width: `${Math.round(a.progress * 100)}%` }} /></div>
                    <div className="text-[10px] text-muted-foreground mt-1">{a.value}/{a.target}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function RewardCard({ reward, balance, onRedeem, loading }) {
  const Icon = ICONS[reward.icon] || Gift;
  const affordable = balance >= reward.cost;
  return (
    <motion.div whileHover={{ y: -3 }} className="rounded-2xl border border-riq-border bg-white p-5 flex flex-col">
      <div className="w-11 h-11 rounded-xl bg-riq-sage flex items-center justify-center mb-3"><Icon size={22} weight="duotone" className="text-riq-primary" /></div>
      <div className="font-head font-semibold text-riq-primary">{reward.title}</div>
      <div className="text-xs text-muted-foreground mt-1 flex-1">{reward.description}</div>
      <div className="flex items-center justify-between mt-4">
        <span className="inline-flex items-center gap-1.5 font-head font-bold text-riq-primary"><Coins size={16} weight="fill" className="text-riq-gold" /> {reward.cost}</span>
        <button
          data-testid={`redeem-${reward.id}`}
          disabled={!affordable || loading}
          onClick={() => onRedeem(reward)}
          className="rounded-full bg-riq-primary text-white px-4 py-2 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5 transition-transform"
        >
          {loading ? "…" : affordable ? "Redeem" : `Need ${reward.cost - balance}`}
        </button>
      </div>
    </motion.div>
  );
}
